import { logger } from './logging';
import type { CalculationResults } from '../types';

const HUBSPOT_PORTAL_ID = '6481357';
const HUBSPOT_FORM_ID = '16eeb4fa-93f6-4d88-a1fd-c5be1d2b8175';

interface CalculatorData {
  currentReserveFund: number;
  annualDuesAndFees: number;
  annualExpenses: number;
  recentInvestmentReturns: number;
  timeRangeYears: number;
}

interface UserInteractionData {
  calculationTimestamp: string;
  timeSpentOnPage: number;
  selectedStrategy: string;
  downloadedPDF: boolean;
}

interface HubSpotFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  hoaName: string;
  message: string;
  calculatorData?: CalculatorData;
  results?: CalculationResults;
  interactionData?: UserInteractionData;
}

interface HubSpotResponse {
  redirectUri?: string;
  inlineMessage?: string;
  status?: string;
}

export async function submitToHubSpot(data: HubSpotFormData): Promise<HubSpotResponse> {
  try {
    logger.info('Starting HubSpot form submission', {
      email: data.email,
      hoaName: data.hoaName,
      hasCalculatorData: !!data.calculatorData,
      hasResults: !!data.results
    });

    const fields = [
      // Contact Information
      { name: 'firstname', value: data.firstName },
      { name: 'lastname', value: data.lastName },
      { name: 'email', value: data.email },
      { name: 'phone', value: data.phone },
      { name: 'company', value: data.hoaName },
      { name: 'message', value: data.message }
    ];

    // Add calculator data if available
    if (data.calculatorData) {
      fields.push(
        { name: 'calculator_current_reserve_fund', value: data.calculatorData.currentReserveFund },
        { name: 'calculator_annual_dues', value: data.calculatorData.annualDuesAndFees },
        { name: 'calculator_annual_expenses', value: data.calculatorData.annualExpenses },
        { name: 'calculator_current_return_rate', value: data.calculatorData.recentInvestmentReturns },
        { name: 'calculator_projection_years', value: data.calculatorData.timeRangeYears }
      );
    }

    // Add results data if available
    if (data.results) {
      const lastIndex = data.results.dates.length - 1;
      const getReturn = (values: number[]) => 
        ((values[lastIndex] - values[0]) / values[0]) * 100;

      fields.push(
        { name: 'calculator_conservative_value', value: data.results.risk_free[lastIndex] },
        { name: 'calculator_low_risk_value', value: data.results.low_risk[lastIndex] },
        { name: 'calculator_moderate_risk_value', value: data.results.moderate_risk[lastIndex] },
        { name: 'calculator_higher_risk_value', value: data.results.high_risk[lastIndex] },
        { name: 'calculator_conservative_return', value: getReturn(data.results.risk_free) },
        { name: 'calculator_low_risk_return', value: getReturn(data.results.low_risk) },
        { name: 'calculator_moderate_risk_return', value: getReturn(data.results.moderate_risk) },
        { name: 'calculator_higher_risk_return', value: getReturn(data.results.high_risk) }
      );
    }

    // Add interaction data if available
    if (data.interactionData) {
      fields.push(
        { name: 'calculator_timestamp', value: data.interactionData.calculationTimestamp },
        { name: 'calculator_time_spent', value: data.interactionData.timeSpentOnPage },
        { name: 'calculator_selected_strategy', value: data.interactionData.selectedStrategy },
        { name: 'calculator_downloaded_pdf', value: data.interactionData.downloadedPDF }
      );
    }

    const requestBody = {
      fields,
      context: {
        pageUri: window.location.href,
        pageName: document.title
      }
    };

    logger.info('Submitting to HubSpot API', {
      portalId: HUBSPOT_PORTAL_ID,
      formId: HUBSPOT_FORM_ID,
      fields: fields.map(f => ({ name: f.name, hasValue: !!f.value }))
    });

    const response = await fetch(
      `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_ID}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      }
    );

    const responseData = await response.text();
    let parsedResponse: HubSpotResponse = {};
    
    try {
      parsedResponse = responseData ? JSON.parse(responseData) : {};
    } catch (e) {
      logger.warn('Failed to parse HubSpot response', { responseData });
    }

    logger.info('HubSpot API response', {
      status: response.status,
      statusText: response.statusText,
      redirectUri: parsedResponse.redirectUri,
      inlineMessage: parsedResponse.inlineMessage
    });

    if (!response.ok) {
      throw new Error(`HubSpot API error: ${response.status} ${response.statusText}\n${responseData}`);
    }

    return parsedResponse;

  } catch (error) {
    logger.error('HubSpot form submission failed', error instanceof Error ? error : new Error('Unknown error'));
    throw error;
  }
}